import { useMemo, useCallback, useDeferredValue } from 'react'
import { Link } from '@app/components/stateless/typo/link'

import { WebsiteSecondary } from '../cells/render'
import { Timer } from '../timer'
import { useWasmContext } from '@app/components/providers'
import { GrValidate } from 'react-icons/gr'
import { HomeManager } from '@app/managers'
import { useInteractiveContext } from '@app/components/providers/interactive'
import { useWebsiteLiveData } from '@app/data/formatters/use-live-data'
import { Score } from '../blocks/score'
import { WebsiteInteractiveBlock } from '../cells/website-cell-interactive'
import { classNames } from '@app/utils/classes'
import { TopBlock } from '../cells/blocks/top-block'
import { useTranslation } from '@app/lib/hooks/useTranslation'
import Trans from 'next-translate/Trans'
import { routeParseAbsolute } from '@app/lib/router-handler'
import type { WebsiteDashboardCellProps } from '@app/types/websitelist'
import type { Pages } from '@app/types'
import { styles } from '@app/styles/dashboard-styles'
import { WebsiteTableMock } from './tables/dashboard-table-mock'
import { ProxyBoxMock } from './cells/proxy-box-input-mock'
import { UserAgentBoxMock } from './cells/ua-box-input-mock'
import { CrawlDelayBoxMock } from './cells/crawl-delay-input-mock'
import { RootSelectorBoxMock } from './cells/root-selector-input-mock'
import { useDomainLinks } from '@app/data/formatters/use-domain-links'
import { useTransDashboard } from '@app/data/translations/use-trans-dashboard'
import { MonitoringEnabledBoxMock } from './cells/monitoring-inpout-mock'
import { RunnersBoxMock } from './cells/runner-input-mock'
import { HideElementsBoxWrapperInput } from '../inputs/hide-elements-input'
import { RobotsInput } from '../inputs/robots-input'
import { MobileInput } from '../inputs/mobile-input'
import { TLDInput } from '../inputs/tld-input'
import { SubDomainsInput } from '../inputs/subdomains-input'
import { StandardInput } from '../inputs/standard-input'
import { SitemapInput } from '../inputs/sitemap-input'
import { SelectThemeInput } from '../inputs/theme-input'
import { RenderAnalyticsMock } from './analytics/website-analytics-mock'
import { MoreOptionsMock } from './options/more-mock'
import { CrawlLimitBoxInput } from '../inputs/crawl-limit-input'
import { BlacklistInput } from '../inputs/blacklist-input'
import { ModalOptions } from '../cells/enums'
import { buildFormatter } from '@app/lib/utils/price-config'
import { Popover, PopoverContent } from '@app/@/components/ui/popover'
import { IgnoreRulesBoxMock } from './cells/ignore-rules-mock'
import { toast } from '@app/@/components/ui/use-toast'

// mock default account data
const account = {
  activeSubscription: true,
  authed: false,
  alertEnabled: true,
  inited: true,
  analyticsDisabled: false,
}

const gridCellStyle = 'py-1 px-1'
const cellStyle = classNames(styles.grid, gridCellStyle)

// main dashboard cell with details and paginated views DO-NOT MEMO
export function WebsiteCellDashboardMock({
  url,
  handleClickOpen,
  pages,
  issues: currentIssues,
  issuesInfo,
  pageLoadTime,
  lastScanDate,
  pageHeaders,
  index,
  domain,
  online,
  activeCrawl,
  crawlDuration,
  ua,
  actions,
  robots,
  subdomains,
  tld,
  shutdown,
  verified,
  runners,
  proxy,
  monitoringEnabled,
  crawlDelay,
  rootselector,
  translation,
  onlyLive, // mobile,
  // standard,
  // sitemap,
  // hideElements,
  // theme,
}: WebsiteDashboardCellProps) {
  const { t, lang } = useTranslation(translation || 'dashboard')
  const { feed } = useWasmContext()

  const { setSelectedWebsite, selectedWebsite } = useInteractiveContext()

  const { linkUrl, domainHost } = useDomainLinks({ url })

  const items: Pages[] = useDeferredValue(
    feed?.get_data_item(domain, !!(tld || subdomains)) ?? []
  )

  const {
    transIssue,
    transIssues,
    transPage,
    transPages,
    transWarning,
    transWarnings,
  } = useTransDashboard(t)

  const issues = (items.length ? items : currentIssues) as Pages[]

  const { errorCount, warningCount, totalIssues, liveData } =
    useWebsiteLiveData({ issues, issuesInfo })

  const onRemovePress = useCallback(async () => {
    if (onlyLive) {
      return
    }
    if (url === selectedWebsite) {
      HomeManager.setDashboardView('')
      setSelectedWebsite('')
    }
  }, [url, selectedWebsite, setSelectedWebsite, onlyLive])

  const handleMainClick =
    (
      eventData?: any,
      title?: string | ModalOptions,
      _mini?: boolean,
      url?: string
    ) =>
    async () => {
      // block modal on only live examples
      if (onlyLive) {
        return
      }
      // mini player open - small modal with dynamic content
      let eventDS = eventData

      if (title === ModalOptions.VerifyDns && url) {
      }

      // TODO: fix title handling with prep for locals
      if (title === ModalOptions.Analytics) {
      }

      if (handleClickOpen) {
        handleClickOpen(eventDS, title, url, null)
      }
    }

  // issuesInfo
  const {
    pageCount = 0,
    totalIssues: issueTotal = 0,
    accessScoreAverage = 0,
  } = issuesInfo ?? {
    pageCount: 0,
    totalIssues: 0,
    accessScoreAverage: 0,
  }

  const pageIssueCount =
    issues && Array.isArray(issues) && issues.length > pageCount
      ? issues.length
      : pageCount

  const scanProgressText = t('scan-progress')

  const onWebsiteCrawl = useCallback(async () => {
    toast({ title: scanProgressText })
  }, [scanProgressText, toast])

  const formatter = useMemo(() => buildFormatter(lang, true), [lang])

  return (
    <li>
      <>
        <Popover>
          <>
            <div>
              <div className='flex gap-x-1 place-items-center place-content-between ltr:pr-3 rtl:pl-3'>
                <div className='flex gap-2 md:gap-4 flex-wrap ltr:pr-2 rtl:pl-2'>
                  <div className='bg-[rgb(234,234,234)] dark:bg-gray-900 place-items-center flex'>
                    <div className='text-center pl-3.5 pr-3.5'>
                      <Score score={accessScoreAverage} />
                    </div>
                  </div>
                  <div
                    className={`flex place-items-center truncate flex-wrap px-1`}
                  >
                    <div className='space-y-1 ltr:pr-1 rtl:pl-1 py-1'>
                      <Link
                        href={'#'}
                        className={classNames(
                          styles.title,
                          'hover:text-blue-700 p-0 flex place-items-center gap-x-1 max-w-[60vw] md:max-w-[75vw] font-semibold'
                        )}
                      >
                        {domainHost}
                        {verified ? (
                          <GrValidate
                            className='grIcon text-sm'
                            title={`${url} is verified.`}
                          />
                        ) : null}
                      </Link>
                      <WebsiteSecondary
                        domain={domain}
                        issuesInfo={{
                          ...issuesInfo,
                          totalIssues:
                            totalIssues > issueTotal ? totalIssues : issueTotal,
                        }}
                        pageIssueCount={pageIssueCount}
                        pageLoadTime={pageLoadTime}
                        lastScanDate={lastScanDate}
                        score={accessScoreAverage}
                        pageHeaders={pageHeaders}
                        robots={robots}
                        subdomains={subdomains}
                        tld={tld}
                        dashboard
                        online={online}
                        borderLess
                      >
                        <Timer
                          noui
                          stop={!activeCrawl}
                          duration={crawlDuration}
                          shutdown={shutdown}
                          t={t}
                          lang={lang}
                        />
                      </WebsiteSecondary>
                    </div>
                  </div>
                </div>
                <MoreOptionsMock
                  url={url}
                  crawlWebsite={onWebsiteCrawl}
                  handleMainClick={handleMainClick}
                  removePress={onRemovePress}
                  pages={pages}
                  pageHeaders={pageHeaders}
                  index={index}
                  subdomains={subdomains}
                  tld={tld}
                  shutdown={shutdown}
                  verified={verified}
                />
              </div>
              <WebsiteInteractiveBlock
                url={url}
                linkUrl={linkUrl}
                onWebsiteCrawl={onWebsiteCrawl}
                activeCrawl={activeCrawl}
                crawlDuration={crawlDuration}
                pageHeaders={pageHeaders}
                handleMainClick={handleMainClick}
                actions={actions}
                activeSubscription={account.activeSubscription}
                translation='demo'
                demo
              />
            </div>

            <PopoverContent className='w-screen md:w-full border-2 ltr:md:ml-4 rtl:md:mr-4'>
              {account.activeSubscription ? null : (
                <div className='px-3 py-3 font-semibold border-b dark:border-gray-700'>
                  <Trans
                    i18nKey='dashboard:edit-upgrade'
                    components={[
                      <p key={1} />,
                      <Link
                        className='text-blue-600 hover:text-blue-400 hover:underline'
                        key={2}
                        href={routeParseAbsolute(`/payments`, lang)}
                      />,
                    ]}
                  />
                </div>
              )}
              <div className={cellStyle}>
                <UserAgentBoxMock
                  ua={ua}
                  url={url}
                  activeSubscription={account.activeSubscription}
                />
                <ProxyBoxMock
                  proxy={proxy}
                  url={url}
                  activeSubscription={account.activeSubscription}
                />
                <CrawlDelayBoxMock
                  crawlDelay={crawlDelay}
                  url={url}
                  activeSubscription={account.activeSubscription}
                />
                <RootSelectorBoxMock
                  url={url}
                  rootElement={rootselector}
                  activeSubscription={account.activeSubscription}
                />
                <HideElementsBoxWrapperInput
                  url={url}
                  hideElements={undefined}
                  activeSubscription={account.activeSubscription}
                  translation={'demo'}
                />
                <CrawlLimitBoxInput
                  value={undefined}
                  activeSubscription={account.activeSubscription}
                  url={url}
                />
                <BlacklistInput
                  // blacklist={blacklist}
                  url={url}
                  activeSubscription={account.activeSubscription}
                />
              </div>
              <div className={cellStyle}>
                <MonitoringEnabledBoxMock
                  enabled={monitoringEnabled}
                  activeSubscription={account.activeSubscription}
                  domain={domain}
                />
                <RobotsInput
                  robots={undefined}
                  url={url}
                  activeSubscription={account.activeSubscription}
                  domain={domain}
                />
                <MobileInput
                  mobile={undefined}
                  url={url}
                  activeSubscription={account.activeSubscription}
                  domain={domain}
                />
                <SubDomainsInput
                  subdomains={undefined}
                  url={url}
                  domain={domain}
                  activeSubscription={account.activeSubscription}
                />
                <TLDInput
                  tld={undefined}
                  url={url}
                  activeSubscription={account.activeSubscription}
                  domain={domain}
                  translation='demo'
                />
              </div>
              <div className={cellStyle}>
                <StandardInput
                  standard={undefined}
                  url={url}
                  domain={domain}
                  activeSubscription={account.activeSubscription}
                />
                <SitemapInput
                  sitemap={undefined}
                  url={url}
                  domain={domain}
                  activeSubscription={account.activeSubscription}
                  t={t}
                />
                <RunnersBoxMock
                  url={url}
                  runners={runners}
                  activeSubscription={account.activeSubscription}
                  translation={translation}
                />
                <SelectThemeInput
                  url={url}
                  domain={domain}
                  theme={undefined}
                  activeSubscription={account.activeSubscription}
                />
                <IgnoreRulesBoxMock
                  url={url}
                  domain={domain}
                  activeSubscription={account.activeSubscription}
                  runners={runners}
                />
              </div>
            </PopoverContent>
          </>
        </Popover>

        <div className={'flex overflow-x-auto gap-x-1'}>
          <TopBlock
            count={pageIssueCount ?? 0}
            label={pageIssueCount === 1 ? transPage : transPages}
            formatter={formatter}
          />
          <TopBlock
            count={errorCount}
            label={errorCount === 1 ? transIssue : transIssues}
            formatter={formatter}
          />
          <TopBlock
            count={warningCount}
            label={warningCount === 1 ? transWarning : transWarnings}
            formatter={formatter}
          />
        </div>

        <RenderAnalyticsMock
          account={account}
          issuesInfo={issuesInfo}
          liveData={liveData}
          domain={domain}
          onlyLive={onlyLive}
        />

        <WebsiteTableMock
          issuesInfo={issuesInfo}
          liveData={liveData}
          activeSubscription={account.activeSubscription}
          handleMainClick={handleMainClick}
          url={url}
        />
      </>
    </li>
  )
}
